import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, Trash2, Search, TicketPercent, X, ChevronLeft, ChevronRight } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-custom.css";
import PageHeader from "../../components/ui/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import TimeSpinner from "../../components/ui/TimeSpinner";
import {
  createManageCoupon,
  deleteManageCoupon,
  getManageCoupon,
  getManageCoupons,
  ManageCoupon,
  PaginatedResponse,
  updateManageCoupon,
} from "../../services/manageCouponsApi";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Parse API datetime ("YYYY-MM-DD HH:mm:ss" or ISO) as local Date. */
const parseApiDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const m = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6] || 0),
    );
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toSqlDateTime = (d: Date | null): string | null => {
  if (!d || Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = parseApiDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusBadge = (status?: string) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "active") {
    return <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">Active</span>;
  }
  if (normalized === "expire") {
    return <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">Expired</span>;
  }
  return <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">Inactive</span>;
};

const parseDays = (days: ManageCoupon["days"]): string[] => {
  if (!days) return [];
  if (Array.isArray(days)) return days.map(String);
  try {
    const parsed = JSON.parse(days as string);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const discountLabel = (coupon: ManageCoupon) => {
  const amount = Number(coupon.amount || 0);
  if (coupon.discount_type === "percentage") return `${amount}%`;
  return amount.toFixed(2);
};

/** used_time = kitna use hua; remaining = uses_limit - used (when limited). */
const couponUsage = (coupon: {
  uses_limit?: number | null;
  used_time?: number | null;
  remaining_uses?: number | null;
}) => {
  const used = Math.max(0, Number(coupon.used_time ?? 0));
  const rawLimit = coupon.uses_limit != null ? Number(coupon.uses_limit) : 0;
  const limited = Number.isFinite(rawLimit) && rawLimit > 0;
  if (!limited) {
    return {
      used,
      limit: null as number | null,
      remaining: null as number | null,
      shortLabel: `${used} used`,
      detailLabel: `${used} used · Unlimited`,
    };
  }
  const remaining =
    coupon.remaining_uses != null
      ? Math.max(0, Number(coupon.remaining_uses))
      : Math.max(0, rawLimit - used);
  return {
    used,
    limit: rawLimit,
    remaining,
    shortLabel: `${used}/${rawLimit}`,
    detailLabel: `${used} used · ${remaining} left · ${rawLimit} max`,
  };
};

type CouponFormState = {
  title: string;
  code: string;
  startDate: Date | null;
  endDate: Date | null;
  usesLimit: string;
  discountType: "percentage" | "amount";
  amount: string;
  minimumPurchase: string;
  description: string;
  status: "active" | "inactive" | "expire";
  selectedDays: Set<string>;
};

const emptyForm = (): CouponFormState => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return {
    title: "",
    code: "",
    startDate: startOfToday,
    endDate: null,
    usesLimit: "",
    discountType: "percentage",
    amount: "",
    minimumPurchase: "0",
    description: "",
    status: "active",
    selectedDays: new Set<string>(WEEKDAYS),
  };
};

const CouponDateTimeField: React.FC<{
  label: string;
  required?: boolean;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder: string;
  minDate?: Date | null;
  allowClear?: boolean;
}> = ({ label, required, value, onChange, placeholder, minDate, allowClear }) => (
  <div>
    <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    <div className="relative flex gap-2">
      <DatePicker
        selected={value}
        onChange={(date: Date | null) => onChange(date)}
        showTimeInput
        customTimeInput={<TimeSpinner />}
        dateFormat="dd MMM yyyy, h:mm aa"
        placeholderText={placeholder}
        minDate={minDate || undefined}
        isClearable={false}
        portalId="root"
        popperClassName="coupon-datepicker-popper"
        calendarClassName="custom-datepicker"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
        wrapperClassName="flex-1 min-w-0"
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
          <div className="flex items-center justify-between px-3 py-1 bg-[#885ABB] rounded-t-lg">
            <button
              type="button"
              onClick={decreaseMonth}
              className="p-1 text-white hover:bg-white/20 rounded-lg"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-white font-semibold text-xs uppercase">
              {date.toLocaleString("default", { month: "long", year: "numeric" })}
            </span>
            <button
              type="button"
              onClick={increaseMonth}
              className="p-1 text-white hover:bg-white/20 rounded-lg"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      />
      {allowClear && value ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="shrink-0 h-[38px] w-[38px] inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          title="Clear"
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  </div>
);

const CouponsPage: React.FC = () => {
  const [rows, setRows] = useState<ManageCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginatedResponse<ManageCoupon> | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewCoupon, setViewCoupon] = useState<ManageCoupon | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState<ManageCoupon | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);
  const [formError, setFormError] = useState("");
  const [createForm, setCreateForm] = useState<CouponFormState>(emptyForm);

  const fetchCoupons = async (targetPage = page, searchOverride?: string, statusOverride?: string) => {
    const effectiveSearch = searchOverride ?? search;
    const effectiveStatus = statusOverride ?? status;
    try {
      setLoading(true);
      setError("");
      const res = await getManageCoupons({
        per_page: perPage,
        page: targetPage,
        search: effectiveSearch || undefined,
        status: effectiveStatus || undefined,
      });
      const rowsArray = Array.isArray(res?.data) ? res.data : [];
      setRows(rowsArray);
      setPagination(res);
      setPage(res.current_page || targetPage);
    } catch (e: any) {
      setRows([]);
      setError(e?.response?.data?.message || "Failed to load coupons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const openView = async (id: number) => {
    try {
      setViewOpen(true);
      setViewLoading(true);
      const c = await getManageCoupon(id);
      setViewCoupon(c);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load coupon details.");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDelete) return;
    try {
      await deleteManageCoupon(selectedDelete.id);
      setDeleteOpen(false);
      setSelectedDelete(null);
      await fetchCoupons(pagination?.current_page || 1);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to delete coupon.");
    }
  };

  const openCreateModal = () => {
    setFormMode("create");
    setEditingCouponId(null);
    setFormError("");
    setEditLoading(false);
    setCreateForm(emptyForm());
    setCreateOpen(true);
  };

  const openEditModal = async (id: number) => {
    setFormMode("edit");
    setEditingCouponId(id);
    setFormError("");
    setCreateForm(emptyForm());
    setCreateOpen(true);
    setEditLoading(true);
    try {
      const c = await getManageCoupon(id);
      const days = parseDays(c.days);
      setCreateForm({
        title: c.title || "",
        code: c.code || "",
        startDate: parseApiDate(c.start_date_time) || new Date(),
        endDate: parseApiDate(c.end_date_time),
        usesLimit: c.uses_limit != null && c.uses_limit > 0 ? String(c.uses_limit) : "",
        discountType: (c.discount_type === "amount" ? "amount" : "percentage") as "percentage" | "amount",
        amount: String(c.amount ?? ""),
        minimumPurchase: c.minimum_purchase_amount != null ? String(c.minimum_purchase_amount) : "0",
        description: c.description || "",
        status: ((c.status || "active").toLowerCase() as "active" | "inactive" | "expire") || "active",
        selectedDays: new Set<string>(days.length ? days : WEEKDAYS),
      });
    } catch (e: any) {
      setFormError(e?.response?.data?.message || "Failed to load coupon.");
    } finally {
      setEditLoading(false);
    }
  };

  const closeFormModal = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setFormError("");
    setEditingCouponId(null);
    setEditLoading(false);
  };

  const handleSaveCoupon = async () => {
    setFormError("");
    if (!createForm.title.trim()) return setFormError("Title is required.");
    if (!createForm.code.trim()) return setFormError("Code is required.");
    if (!createForm.startDate) return setFormError("Start date & time is required.");
    if (
      createForm.endDate &&
      createForm.startDate &&
      createForm.endDate.getTime() < createForm.startDate.getTime()
    ) {
      return setFormError("Expiry must be on or after the start date.");
    }
    if (createForm.selectedDays.size === 0) return setFormError("Select at least one day.");
    const amountNum = parseFloat(createForm.amount);
    if (Number.isNaN(amountNum) || amountNum < 0) return setFormError("Valid amount is required.");
    if (createForm.discountType === "percentage" && amountNum > 100) {
      return setFormError("Percentage discount cannot exceed 100.");
    }
    const usesLimitTrimmed = createForm.usesLimit.trim();
    let usesLimitValue: number | null = null;
    if (usesLimitTrimmed !== "") {
      usesLimitValue = parseInt(usesLimitTrimmed, 10);
      if (!Number.isFinite(usesLimitValue) || usesLimitValue < 0) {
        return setFormError("Uses limit must be a valid number (or leave empty for unlimited).");
      }
    }

    try {
      setCreateSaving(true);
      const payload = {
        title: createForm.title.trim(),
        code: createForm.code.trim().toUpperCase(),
        start_date_time: toSqlDateTime(createForm.startDate),
        end_date_time: toSqlDateTime(createForm.endDate),
        uses_limit: usesLimitValue,
        amount: amountNum,
        discount_type: createForm.discountType,
        minimum_purchase_amount: parseFloat(createForm.minimumPurchase || "0") || 0,
        days: Array.from(createForm.selectedDays),
        description: createForm.description.trim() || null,
        status: createForm.status,
      };
      if (formMode === "edit" && editingCouponId) {
        await updateManageCoupon(editingCouponId, payload);
      } else {
        await createManageCoupon(payload);
      }
      setCreateOpen(false);
      setEditingCouponId(null);
      setFormError("");
      await fetchCoupons(1);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === "object") {
        setFormError(Object.values(errs).flat().join(" ") || msg || "Validation failed.");
      } else {
        setFormError(msg || "Could not save coupon.");
      }
    } finally {
      setCreateSaving(false);
    }
  };

  const activeCount = useMemo(
    () => rows.filter((r) => (r.status || "").toLowerCase() === "active").length,
    [rows],
  );
  const expiredCount = useMemo(
    () => rows.filter((r) => (r.status || "").toLowerCase() === "expire").length,
    [rows],
  );
  const inactiveCount = useMemo(
    () => rows.filter((r) => (r.status || "").toLowerCase() === "inactive").length,
    [rows],
  );

  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const total = pagination?.total ?? rows.length;
  const viewDays = viewCoupon ? parseDays(viewCoupon.days) : [];
  const allDaysSelected = createForm.selectedDays.size === WEEKDAYS.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .coupon-datepicker-popper {
          z-index: 10050 !important;
        }
        .coupon-datepicker-popper .react-datepicker__triangle {
          display: none;
        }
      `}</style>
      <div className="p-4 lg:p-6">
        <PageHeader
          title="Coupons"
          description="Manage discount coupons for your company"
          icon={TicketPercent}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={true}
          actions={
            <button
              type="button"
              onClick={openCreateModal}
              className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
            >
              + Create Coupon
            </button>
          }
        />

        <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500">Show</span>
              <select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-500">per page</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expire">Expired</option>
              </select>
              <button
                type="button"
                onClick={() => fetchCoupons(1)}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
              >
                Apply
              </button>
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search coupons..."
                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold text-[#402B69]">{total}</p></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"><p className="text-xs text-gray-500">Active</p><p className="text-2xl font-bold text-green-600">{activeCount}</p></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"><p className="text-xs text-gray-500">Expired</p><p className="text-2xl font-bold text-amber-600">{expiredCount}</p></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"><p className="text-xs text-gray-500">Inactive</p><p className="text-2xl font-bold text-red-600">{inactiveCount}</p></div>
        </div>

        {error ? <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{error}</div> : null}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
          {loading ? (
            <div className="p-6 text-gray-600">Loading coupons...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#885ABB] text-white border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">#</th>
                      <th className="text-left px-4 py-3 font-semibold">Coupon</th>
                      <th className="text-left px-4 py-3 font-semibold">Code</th>
                      <th className="text-left px-4 py-3 font-semibold">Validity</th>
                      <th className="text-left px-4 py-3 font-semibold">Discount</th>
                      <th className="text-left px-4 py-3 font-semibold">Usage</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-right px-4 py-3 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td className="px-4 py-6 text-gray-500" colSpan={8}>No coupons found.</td></tr>
                    ) : (
                      rows.map((row, index) => {
                        const usage = couponUsage(row);
                        return (
                        <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                          <td className="px-4 py-3">{(currentPage - 1) * perPage + index + 1}</td>
                          <td className="px-4 py-3 font-medium text-[#402B69] uppercase">{row.title}</td>
                          <td className="px-4 py-3">{row.code || "-"}</td>
                          <td className="px-4 py-3"><div className="text-xs">Start date: {formatDate(row.start_date_time)}</div><div className="text-xs text-gray-500">Expiry date: {formatDate(row.end_date_time)}</div></td>
                          <td className="px-4 py-3">{discountLabel(row)}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-[#402B69]">{usage.shortLabel}</div>
                            <div className="text-xs text-gray-500">
                              {usage.limit == null
                                ? "Unlimited"
                                : `${usage.remaining} remaining`}
                            </div>
                          </td>
                          <td className="px-4 py-3">{statusBadge(row.status)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => openView(row.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700"><Eye size={15} /></button>
                              <button type="button" onClick={() => openEditModal(row.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"><Pencil size={15} /></button>
                              <button type="button" onClick={() => { setSelectedDelete(row); setDeleteOpen(true); }} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-700"><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="border-t px-4 py-3 flex items-center justify-between">
                <p className="text-xs text-gray-500">Showing page {currentPage} of {lastPage} ({total} total)</p>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => fetchCoupons(currentPage - 1)} disabled={currentPage <= 1 || loading} className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50">Previous</button>
                  <button type="button" onClick={() => fetchCoupons(currentPage + 1)} disabled={currentPage >= lastPage || loading} className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50">Next</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {viewOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b px-4 py-3 bg-gray-50">
              <h3 className="text-lg font-semibold text-[#402B69]">Coupon detail</h3>
              <button type="button" onClick={() => { setViewOpen(false); setViewCoupon(null); }} className="text-sm text-gray-600 hover:text-gray-900">Close</button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              {viewLoading ? <p className="text-gray-600">Loading...</p> : viewCoupon ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="text-xs uppercase text-gray-500">Title</p><p className="font-medium">{viewCoupon.title}</p></div>
                    <div><p className="text-xs uppercase text-gray-500">Code</p><p>{viewCoupon.code || "-"}</p></div>
                    <div><p className="text-xs uppercase text-gray-500">Start date</p><p>{viewCoupon.start_date_time || "-"}</p></div>
                    <div><p className="text-xs uppercase text-gray-500">Expiry date</p><p>{viewCoupon.end_date_time || "-"}</p></div>
                    <div><p className="text-xs uppercase text-gray-500">Uses limit</p><p>{couponUsage(viewCoupon).limit ?? "Unlimited"}</p></div>
                    <div><p className="text-xs uppercase text-gray-500">Used</p><p>{couponUsage(viewCoupon).used}</p></div>
                    <div className="col-span-2 rounded-lg border border-purple-100 bg-purple-50/60 px-3 py-2">
                      <p className="text-xs uppercase text-gray-500">Remaining</p>
                      <p className="text-base font-semibold text-[#402B69]">
                        {couponUsage(viewCoupon).limit == null
                          ? "Unlimited"
                          : `${couponUsage(viewCoupon).remaining} left of ${couponUsage(viewCoupon).limit}`}
                      </p>
                    </div>
                  </div>
                  <div><p className="text-xs uppercase text-gray-500 mb-1">Days for apply</p><p>{viewDays.length === 7 ? "All days" : viewDays.length ? viewDays.join(", ") : "-"}</p></div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => { setDeleteOpen(false); setSelectedDelete(null); }}
        onConfirm={handleDelete}
        title="Delete coupon"
        message={`Are you sure you want to delete coupon "${selectedDelete?.title || ""}"?`}
        confirmText="Delete"
        variant="danger"
      />

      {createOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b px-4 py-3 bg-gray-50 shrink-0">
              <h3 className="text-lg font-semibold text-[#402B69]">
                {formMode === "edit" ? "Edit Coupon" : "Create Coupon"}
              </h3>
              <button
                type="button"
                onClick={closeFormModal}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
              {editLoading ? (
                <p className="text-sm text-gray-600 py-8 text-center">Loading coupon...</p>
              ) : (
                <>
                  {formError ? (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">
                      {formError}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
                        Coupon title <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={createForm.title}
                        onChange={(e) => setCreateForm((s) => ({ ...s, title: e.target.value }))}
                        placeholder="e.g. Summer Sale"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
                        Coupon code <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={createForm.code}
                        onChange={(e) =>
                          setCreateForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))
                        }
                        placeholder="e.g. SUMMER20"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      />
                    </div>

                    <CouponDateTimeField
                      label="Start date & time"
                      required
                      value={createForm.startDate}
                      onChange={(startDate) => setCreateForm((s) => ({ ...s, startDate }))}
                      placeholder="Select start date & time"
                    />
                    <CouponDateTimeField
                      label="Expiry date & time"
                      value={createForm.endDate}
                      onChange={(endDate) => setCreateForm((s) => ({ ...s, endDate }))}
                      placeholder="Optional — leave empty for no expiry"
                      minDate={createForm.startDate}
                      allowClear
                    />

                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">Uses limit</label>
                      <input
                        type="number"
                        min={0}
                        value={createForm.usesLimit}
                        onChange={(e) => setCreateForm((s) => ({ ...s, usesLimit: e.target.value }))}
                        placeholder="Leave empty for unlimited"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">Discount type</label>
                      <select
                        value={createForm.discountType}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            discountType: e.target.value as "percentage" | "amount",
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="amount">Fixed amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
                        {createForm.discountType === "percentage" ? "Percent *" : "Amount *"}
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={createForm.discountType === "percentage" ? 100 : undefined}
                        step="0.01"
                        value={createForm.amount}
                        onChange={(e) => setCreateForm((s) => ({ ...s, amount: e.target.value }))}
                        placeholder={createForm.discountType === "percentage" ? "e.g. 10" : "e.g. 25.00"}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
                        Minimum purchase amount
                      </label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={createForm.minimumPurchase}
                        onChange={(e) =>
                          setCreateForm((s) => ({ ...s, minimumPurchase: e.target.value }))
                        }
                        placeholder="0.00"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#402B69] mb-1.5">Status</label>
                      <select
                        value={createForm.status}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            status: e.target.value as "active" | "inactive" | "expire",
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="expire">Expired</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-[#402B69]">Days for apply</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setCreateForm((s) => ({
                              ...s,
                              selectedDays: new Set<string>(WEEKDAYS),
                            }))
                          }
                          className="text-xs font-semibold text-[#885ABB] hover:underline"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setCreateForm((s) => ({ ...s, selectedDays: new Set<string>() }))
                          }
                          className="text-xs font-semibold text-gray-500 hover:underline"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {WEEKDAYS.map((day) => (
                        <label key={day} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={createForm.selectedDays.has(day)}
                            onChange={() =>
                              setCreateForm((s) => {
                                const next = new Set(s.selectedDays);
                                if (next.has(day)) next.delete(day);
                                else next.add(day);
                                return { ...s, selectedDays: next };
                              })
                            }
                            className="rounded border-gray-300"
                          />
                          {day}
                        </label>
                      ))}
                    </div>
                    {allDaysSelected ? (
                      <p className="text-xs text-gray-500 mt-2">Valid every day of the week.</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#402B69] mb-1.5">Description</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) =>
                        setCreateForm((s) => ({ ...s, description: e.target.value }))
                      }
                      rows={3}
                      placeholder="Optional notes about this coupon"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                    />
                  </div>
                </>
              )}
            </div>

            {!editLoading ? (
              <div className="shrink-0 border-t px-5 py-3 flex gap-2 bg-white rounded-b-xl">
                <button
                  type="button"
                  disabled={createSaving}
                  onClick={handleSaveCoupon}
                  className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                >
                  {createSaving ? "Saving..." : formMode === "edit" ? "Update" : "Save"}
                </button>
                <button
                  type="button"
                  disabled={createSaving}
                  onClick={closeFormModal}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CouponsPage;
